import { useState, useContext, useEffect } from 'react'
import { Button } from '@/components/ui/shadcn/button'
import { Input } from '@/components/ui/shadcn/input'
import { Label } from '@/components/ui/shadcn/label'
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert'
import { CheckCircle2, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { AuthContext } from '@/contexts/AuthContext'
import { fetcher } from '@/lib/api/fetch'
import { strs } from '@/lib/strings'

const DEFAULTS = { critical: 90, important: 180, supporting: 365 }

/**
 * AssumptionFreshnessPanel — customer-facing settings panel for per-criticality
 * freshness windows.
 *
 * Per CLAUDE.md placement rule: customer-facing → Settings, not Admin.
 * Spec: docs/superpowers/specs/2026-06-01-r11-assumption-evidence-first-class-design.md §7.3
 */
export default function AssumptionFreshnessPanel() {
  const authContext = useContext(AuthContext)
  const org = authContext.user?.organization

  const [critical, setCritical] = useState(
    org?.assumptionFreshness?.critical ?? DEFAULTS.critical,
  )
  const [important, setImportant] = useState(
    org?.assumptionFreshness?.important ?? DEFAULTS.important,
  )
  const [supporting, setSupporting] = useState(
    org?.assumptionFreshness?.supporting ?? DEFAULTS.supporting,
  )

  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState(null)

  // Sync when org context refreshes (e.g. after page reload)
  useEffect(() => {
    if (org?.assumptionFreshness) {
      setCritical(org.assumptionFreshness.critical ?? DEFAULTS.critical)
      setImportant(org.assumptionFreshness.important ?? DEFAULTS.important)
      setSupporting(org.assumptionFreshness.supporting ?? DEFAULTS.supporting)
    }
  }, [org?.assumptionFreshness])

  const validate = () => {
    const vals = [critical, important, supporting]
    for (const v of vals) {
      const n = Number(v)
      if (!Number.isInteger(n) || n < 1 || n > 3650) {
        return 'All freshness windows must be integers between 1 and 3650.'
      }
    }
    return null
  }

  const handleSave = async () => {
    const err = validate()
    if (err) {
      setError(err)
      return
    }
    setSaving(true)
    setError(null)
    try {
      const res = await fetcher(
        `/api/admin/organization/${org.id}/settings/assumption-freshness`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            critical: Number(critical),
            important: Number(important),
            supporting: Number(supporting),
          }),
        },
        authContext,
      )
      const data = await res.json()
      if (!res.ok) {
        setError(data.error || 'Failed to save freshness settings')
        return
      }

      // Update the auth context so settings persist within the session
      authContext.setuser(
        {
          ...authContext.user,
          organization: {
            ...authContext.user.organization,
            assumptionFreshness: {
              critical: Number(critical),
              important: Number(important),
              supporting: Number(supporting),
            },
          },
        },
        () => {},
      )

      setSaved(true)
      setTimeout(() => setSaved(false), 10000)
      toast.success('Freshness windows saved')
    } catch (e) {
      setError(e.message || 'Failed to save freshness settings')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-4">
        <h3 className="text-sm font-semibold">
          {strs('Assumption-Freshness-Settings-title', org)}
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {strs('Assumption-Freshness-Settings-subtitle', org)}
        </p>
      </div>

      {saved && (
        <Alert className="mb-3 border-emerald-200 bg-emerald-50 text-emerald-900">
          <CheckCircle2 className="size-4" />
          <AlertDescription>Freshness windows saved</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive" className="mb-3">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="freshness-critical">
            {strs('Critical Assumption', org)} (days)
          </Label>
          <Input
            id="freshness-critical"
            type="number"
            min={1}
            max={3650}
            value={critical}
            onChange={e => setCritical(e.target.value)}
            placeholder={String(DEFAULTS.critical)}
          />
          <p className="text-xs text-muted-foreground">
            Default: {DEFAULTS.critical} days
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="freshness-important">
            {strs('Important Assumption', org)} (days)
          </Label>
          <Input
            id="freshness-important"
            type="number"
            min={1}
            max={3650}
            value={important}
            onChange={e => setImportant(e.target.value)}
            placeholder={String(DEFAULTS.important)}
          />
          <p className="text-xs text-muted-foreground">
            Default: {DEFAULTS.important} days
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="freshness-supporting">
            {strs('Supporting Assumption', org)} (days)
          </Label>
          <Input
            id="freshness-supporting"
            type="number"
            min={1}
            max={3650}
            value={supporting}
            onChange={e => setSupporting(e.target.value)}
            placeholder={String(DEFAULTS.supporting)}
          />
          <p className="text-xs text-muted-foreground">
            Default: {DEFAULTS.supporting} days
          </p>
        </div>
      </div>

      <div className="mt-4">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="size-3.5 animate-spin mr-1" />
              Saving…
            </>
          ) : (
            'Save'
          )}
        </Button>
      </div>
    </div>
  )
}
